/**
 * Checklist Repository Implementation
 * Implements the ChecklistRepository interface
 * Coordinates between API calls and mappers
 */

import { ChecklistRepository } from "../../domain/repositories/ChecklistRepository";
import {
  ChecklistCountsEntity,
  ChecklistProgressEntity,
  ChecklistItemsResultEntity,
  ChecklistAttachmentEntity,
  ChecklistItemsTabStatus,
} from "../../domain/entities/ChecklistEntity";
import { MONTH_END_CHECKLIST_ALL_AREAS_KEY } from "../../../../shared/constants/monthEndChecklist";
import {
  getChecklistCounts as getChecklistCountsApi,
  getChecklistProgress as getChecklistProgressApi,
  getChecklistItemsByArea as getChecklistItemsByAreaApi,
  getChecklistItemsAllAreas as getChecklistItemsAllAreasApi,
  searchChecklistItemsByTitleInArea as searchChecklistItemsByTitleInAreaApi,
  searchChecklistItemsByTitleAllAreas as searchChecklistItemsByTitleAllAreasApi,
  markItemDone as markItemDoneApi,
  markItemInProgress as markItemInProgressApi,
  markItemToBeDoneLater as markItemToBeDoneLaterApi,
  uploadAttachment as uploadAttachmentApi,
  deleteAttachment as deleteAttachmentApi,
  closeMonth as closeMonthApi,
  triggerCronClientMonth as triggerCronClientMonthApi,
} from "../api/checklistApi";
import { ChecklistMapper } from "../mappers/ChecklistMapper";

export class ChecklistRepositoryImpl implements ChecklistRepository {
  /**
   * Get checklist counts grouped by accounting area and status
   */
  async getCounts(month?: string): Promise<ChecklistCountsEntity> {
    const response = await getChecklistCountsApi(month);
    return ChecklistMapper.toChecklistCountsEntity(response);
  }

  /**
   * Get checklist progress statistics
   */
  async getProgress(month?: string): Promise<ChecklistProgressEntity> {
    const response = await getChecklistProgressApi(month);
    return ChecklistMapper.toChecklistProgressEntity(response);
  }

  /**
   * Get paginated checklist items by accounting area
   */
  async getItemsByArea(
    month: string,
    accountingArea: string,
    offset: number,
    limit: number,
    status?: ChecklistItemsTabStatus | null,
  ): Promise<ChecklistItemsResultEntity> {
    const response =
      accountingArea === MONTH_END_CHECKLIST_ALL_AREAS_KEY
        ? await getChecklistItemsAllAreasApi(month, offset, limit, status)
        : await getChecklistItemsByAreaApi(
            month,
            accountingArea,
            offset,
            limit,
            status,
          );
    return ChecklistMapper.toChecklistItemsResultEntity(response);
  }

  /**
   * Search checklist items by title within an accounting area (paginated).
   */
  async searchItemsByTitleInArea(
    month: string,
    accountingArea: string,
    titleQuery: string,
    offset: number,
    limit: number,
    status?: ChecklistItemsTabStatus | null,
  ): Promise<ChecklistItemsResultEntity> {
    const response =
      accountingArea === MONTH_END_CHECKLIST_ALL_AREAS_KEY
        ? await searchChecklistItemsByTitleAllAreasApi(
            month,
            titleQuery,
            offset,
            limit,
            status,
          )
        : await searchChecklistItemsByTitleInAreaApi(
            month,
            accountingArea,
            titleQuery,
            offset,
            limit,
            status,
          );
    return ChecklistMapper.toChecklistItemsResultEntity(response);
  }

  /**
   * Mark a checklist item as done
   */
  async markItemDone(
    itemId: number,
    notes?: string,
    description?: string,
  ): Promise<void> {
    await markItemDoneApi(itemId, notes, description);
  }

  /**
   * Mark a checklist item as in progress
   */
  async markItemInProgress(itemId: number): Promise<void> {
    await markItemInProgressApi(itemId);
  }

  async markItemToBeDoneLater(itemId: number): Promise<void> {
    await markItemToBeDoneLaterApi(itemId);
  }

  /**
   * Upload an attachment (screenshot) for a checklist item
   */
  async uploadAttachment(
    itemId: number,
    file: File,
  ): Promise<ChecklistAttachmentEntity> {
    const response = await uploadAttachmentApi(itemId, file);
    return ChecklistMapper.toChecklistAttachmentEntity(response.data);
  }

  /**
   * Delete an attachment
   */
  async deleteAttachment(attachmentId: number): Promise<void> {
    await deleteAttachmentApi(attachmentId);
  }

  /**
   * Close the month (mark checklist as closed)
   */
  async closeMonth(checklistId: number): Promise<void> {
    await closeMonthApi(checklistId);
  }

  /**
   * Trigger CFO cron_client_month (generate monthly report and persist mapped values)
   */
  async triggerCronClientMonth(year: number, month: number): Promise<void> {
    await triggerCronClientMonthApi(year, month);
  }
}
